import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import styles from './ValuesForSet.scss'
import { CircleSpinner } from 'common/spinners'
import { CTA } from 'common/styleguide/CTA'
import { tmsValueActions, tmsValueSelectors } from 'data/resources/tmsValues'
import { TmsValue } from 'data/records'
import { Map, OrderedMap } from 'immutable'
import { Popover } from 'common/styleguide/Popover'
import { useHover } from 'lib/hooks/useHover'

import flagUS from 'common/modals/ChangeLocaleModal/us-icon.svg'
import flagES from 'common/modals/ChangeLocaleModal/spain-icon.svg'
import flagFR from 'common/modals/ChangeLocaleModal/france-icon.svg'
import flagDE from 'common/modals/ChangeLocaleModal/germany-icon.svg'
import flagNL from 'common/modals/ChangeLocaleModal/ned-icon.svg'
import flagJA from 'common/modals/ChangeLocaleModal/japan-icon.svg'
import flagIT from 'common/modals/ChangeLocaleModal/it-icon.svg'

const flags = Map({
  en: flagUS,
  es: flagES,
  fr: flagFR,
  de: flagDE,
  nl: flagNL,
  ja: flagJA,
  it: flagIT
})

const langs = OrderedMap({
  fr: 'French',
  it: 'Italian',
  nl: 'Dutch',
  es: 'Spanish',
  ja: 'Japanese',
  de: 'German'
})

export const _ValuesForSet = ({ tmsSetId, loadValues, valuesForSet, createValue, updateValue }) => {
  useEffect(() => {
    loadValues()
  }, [])

  if (valuesForSet.size === 0) return <CircleSpinner centered size={40} /> // certain to have values because we create blank ones

  return <div className={styles.ValuesForSet}>
    {langs.entrySeq().map(([key, val]) => (
      <ValueRow
        key={key}
        tmsSetId={tmsSetId}
        lang={key}
        valuesForSet={valuesForSet}
        createValue={createValue}
        updateValue={updateValue} />
    ))}
  </div>
}

const ValueRow = ({ tmsSetId, valuesForSet, lang, updateValue, createValue }) => {
  const value = valuesForSet.find(v => v.language === lang)
  const initialValue = value?.value
  const [editing, setEditing] = useState(false)
  const [v, setV] = useState(value?.value)
  const [hoverRef, isHovered] = useHover()

  useEffect(() => {
    if (value?.value !== v) {
      setV(value?.value)
    }
  }, [value])

  const onSave = () => {
    value
      ? v !== value?.value && updateValue(value.set('value', v))
      : createValue(TmsValue({ tmsSetId, value: v }))
    setEditing(false)
  }

  const toggleEditing = () => {
    if (v !== initialValue) {
      setV(initialValue)
    }
    setEditing(!editing)
  }

  const target = useRef(null)

  return <div className={styles.langRow}>
    <span ref={hoverRef}>
      <img className={styles.langFlag} src={flags.get(lang)} ref={target} />
    </span>
    {isHovered && <Popover
      target={target.current}
      placement='left'
    >
      {lang}
    </Popover>}

    {editing ? <div className={styles.editingContainer}>
      <form onSubmit={onSave}>
        <textarea className={styles.translationForm} value={v} onChange={e => setV(e.target.value)} />
      </form>
      <div className={styles.ctas}>
        <CTA link margins={false} onClick={onSave}>save</CTA>
        <CTA link margins={false} onClick={toggleEditing}>cancel</CTA>
      </div>
    </div>
      : <>
        <span>{v ?? 'no translated value yet'}</span>
        <div className={styles.ctas}>
          <CTA link margins={false} onClick={toggleEditing}>edit</CTA>
        </div>
       </>
    }
  </div>
}

const mapStateToProps = (state, ownProps) => ({
  valuesForSet: tmsValueSelectors.byTmsSetId(state, ownProps.tmsSetId)
})

const mapDispatchToProps = (dispatch, ownProps) => ({
  loadValues: () => dispatch(tmsValueActions.list({ tmsSetId: ownProps.tmsSetId })),
  createValue: params => dispatch(tmsValueActions.create(params)),
  updateValue: ({ id, value }) => dispatch(tmsValueActions.update(id, TmsValue.serialize(TmsValue({ value }), ['value'])))
})

export const ValuesForSet = connect(
  mapStateToProps,
  mapDispatchToProps
)(_ValuesForSet)
